import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type MarkTestPaper } from '../../api';

/**
 * Marking one paper.
 *
 * The whole paper is shown, but only the questions the machine could not score
 * are editable. The running total updates as marks are typed, so the person
 * marking can see the score they are about to give before they give it.
 */
export function MarkTest() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState<MarkTestPaper | null>(null);
  const [awards, setAwards] = useState<Record<number, { points: string; comment: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ score: number | null; passed: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      const p = await api.get<MarkTestPaper>(`/instructor/marking/tests/${attemptId}`);
      setPaper(p);
      const blank: Record<number, { points: string; comment: string }> = {};
      for (const q of p.questions) if (q.needsReview) blank[q.id] = { points: '', comment: '' };
      setAwards(blank);
    } catch (e) { setError((e as ApiError).message); }
  }, [attemptId]);
  useEffect(() => { void load(); }, [load]);

  if (error && !paper) return <p className="auth__error">{error}</p>;
  if (!paper) return <p className="muted">Loading…</p>;

  const review = paper.questions.filter((q) => q.needsReview);
  const autoPoints = paper.questions.filter((q) => !q.needsReview).reduce((a, q) => a + q.points, 0);
  const handPoints = review.reduce((a, q) => {
    const typed = Number(awards[q.id]?.points);
    return a + (Number.isFinite(typed) ? Math.max(0, Math.min(typed, q.weight)) : 0);
  }, 0);
  const possible = paper.questions.reduce((a, q) => a + q.weight, 0);
  const projected = possible > 0 ? Math.round(((autoPoints + handPoints) / possible) * 100) : null;
  const allMarked = review.every((q) => awards[q.id]?.points.trim() !== '' && Number.isFinite(Number(awards[q.id]?.points)));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const body = {
        awards: Object.fromEntries(review.map((q) => [
          String(q.id),
          { points: Number(awards[q.id].points), comment: awards[q.id].comment.trim() || undefined },
        ])),
      };
      const r = await api.post<{ score: number | null; passed: boolean }>(`/instructor/marking/tests/${attemptId}`, body);
      setDone({ score: r.score, passed: r.passed });
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs">
        <Link to="/marking">To mark</Link> <span aria-hidden>›</span> {paper.unit.name}
      </nav>

      <div className="page-head">
        <h1 className="h1">{paper.learner.firstName} {paper.learner.lastName}</h1>
        <span className="muted">
          {paper.course.name} · {paper.unit.name} · attempt {paper.attempt.attemptNo} · pass mark {paper.attempt.passMark}%
        </span>
      </div>

      {done && (
        <p className="notice">
          Marked: <strong>{done.score}%</strong> — {done.passed ? 'passed' : 'not passed'}.
          The trainee's record has been updated. <Link to="/marking">Back to the queue</Link>
        </p>
      )}
      {error && <p className="auth__error">{error}</p>}

      {review.length === 0 && !done && (
        <p className="notice">Nothing on this paper needs a person. It was marked in full when it was handed in.</p>
      )}

      {review.length > 0 && (
        <p className="notice">
          {review.length} {review.length === 1 ? 'answer needs' : 'answers need'} reading.
          Automatic marks so far: {autoPoints} of {possible}.
          {projected != null && <> Score if you save now: <strong>{projected}%</strong>.</>}
        </p>
      )}

      <ol className="paper">
        {paper.questions.map((q, i) => (
          <li key={q.id} className={`paper__q ${q.needsReview ? 'is-review' : ''}`}>
            <div className="paper__head">
              <span className="muted small">Question {i + 1} · worth {q.weight}</span>
              {!q.needsReview && (
                <span className={`pill ${q.correct ? 'pill--completed' : 'pill--incomplete'}`}>
                  {q.correct === null ? 'not scored' : q.correct ? 'correct' : 'wrong'} · {q.points}
                </span>
              )}
            </div>

            <div className="prose" dangerouslySetInnerHTML={{ __html: q.textHtml }} />

            <div className="paper__answer">
              <span className="muted small">What they wrote</span>
              {q.given.length === 0 || q.given.every((g) => !g?.trim())
                ? <p className="muted">Left blank.</p>
                : <ul>{q.given.map((g, n) => <li key={n}>{g}</li>)}</ul>}
            </div>

            {q.expected.length > 0 && (
              <div className="paper__answer">
                <span className="muted small">Correct answer</span>
                <ul>{q.expected.map((g, n) => <li key={n}>{g}</li>)}</ul>
              </div>
            )}

            {q.needsReview && (
              <div className="form form--inline">
                <label><span>Marks out of {q.weight}</span>
                  <input type="number" min={0} max={q.weight} step="0.5" inputMode="decimal"
                    value={awards[q.id]?.points ?? ''}
                    onChange={(e) => setAwards({ ...awards, [q.id]: { ...awards[q.id], points: e.target.value } })} />
                </label>
                <label className="grow"><span>Comment for the trainee (optional)</span>
                  <input value={awards[q.id]?.comment ?? ''}
                    onChange={(e) => setAwards({ ...awards, [q.id]: { ...awards[q.id], comment: e.target.value } })} />
                </label>
              </div>
            )}

            {!q.needsReview && q.comment && <p className="muted small">Marker's note: {q.comment}</p>}
          </li>
        ))}
      </ol>

      {review.length > 0 && !done && (
        <div className="row">
          <button className="btn btn--primary" disabled={busy || !allMarked} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save marks'}
          </button>
          <button className="btn btn--ghost" onClick={() => navigate('/marking')}>Cancel</button>
          {!allMarked && <span className="muted small">Give every outstanding answer a mark first.</span>}
        </div>
      )}
    </>
  );
}
